// PaymentForm — provider-AGNOSTIC. Reads a CheckoutResponse and hands
// the relevant pieces to ProviderCardField (the one component allowed
// to know which provider is active).
//
// In v1 the "Pay" click is simulated through the mock provider's
// webhook receiver — the real Stripe / Chase wiring would replace
// the submit handler with the provider's SDK confirm-payment call
// inside ProviderCardField itself.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../../api/client.js';
import type { CheckoutResponse } from '../../api/submission.js';
import * as testTools from '../../api/testTools.js';
import { useAppEnvironment } from '../../config/ConfigContext.js';
import { ProviderCardField } from './ProviderCardField.js';

type Props = {
  checkout: CheckoutResponse;
  onPaymentInitiated?: () => void;
  // Override the mock-provider success simulation. The authenticated
  // customer flow uses the default (JWT-gated /dev/mock-payment-success);
  // the account-less partner pay-token flow passes a variant that hits
  // the pay-token-authorized /pay/mock-payment-success instead.
  simulateMockSuccess?: (checkout: CheckoutResponse) => Promise<void>;
};

async function defaultSimulateMockSuccess(checkout: CheckoutResponse): Promise<void> {
  await api.post('/dev/mock-payment-success', {
    provider_transaction_id: checkout.client_action_token,
    amount_cents: checkout.amount_cents,
  });
}

export function PaymentForm({
  checkout,
  onPaymentInitiated,
  simulateMockSuccess = defaultSimulateMockSuccess,
}: Props): JSX.Element {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { t } = useTranslation(['checkout', 'common']);
  const appEnv = useAppEnvironment();
  const isTestLane = appEnv === 'local' || appEnv === 'stage';

  // In local/stage, Stripe can't deliver payment_intent.succeeded to
  // localhost (and stage test charges may have no webhook route), so the
  // quote would never flip to 'paid' and Checkout would poll forever.
  // Simulate the webhook through the same idempotent server path a real
  // delivery uses. Best-effort — the charge is real regardless; if this
  // fails we still start polling (a real webhook may yet arrive on stage).
  async function settleInTestLane(provider_transaction_id?: string): Promise<void> {
    if (!isTestLane || !provider_transaction_id) return;
    try {
      await testTools.settlePayment(provider_transaction_id, checkout.amount_cents);
    } catch {
      // swallow — polling continues; see comment above.
    }
  }

  // Mock-only flow: ask the backend to fire its own webhook so the
  // Payment row transitions to 'completed' and the Quote becomes 'paid'.
  // Real SDK providers (Stripe) confirm client-side inside
  // ProviderCardField and call onProviderSuccess/onProviderError instead.
  async function handleMockPay(): Promise<void> {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await simulateMockSuccess(checkout);
      onPaymentInitiated?.();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : t('paymentForm.errors.paymentFailed'));
      setSubmitting(false);
    }
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{t('paymentForm.title')}</h3>
      {errorMessage && (
        <div className="banner banner--error">{errorMessage}</div>
      )}
      <ProviderCardField
        provider_name={checkout.provider}
        client_action_token={checkout.client_action_token}
        amount_cents={checkout.amount_cents}
        currency={checkout.currency}
        disabled={submitting}
        // Mock: the Pay button runs the webhook-simulation flow.
        onSubmit={() => void handleMockPay()}
        // Stripe: the card field confirmed the charge — start polling for
        // the webhook-driven 'paid' transition (settling it ourselves first
        // in test lanes, where no webhook reaches localhost).
        onProviderSuccess={(provider_transaction_id) => {
          setSubmitting(true);
          setErrorMessage(null);
          onPaymentInitiated?.();
          void settleInTestLane(provider_transaction_id);
        }}
        onProviderError={(m) => setErrorMessage(m || null)}
        onSubmittingChange={setSubmitting}
      />
    </div>
  );
}
